<template>
    <component
      v-bind:is="canAdd ? 'v-combobox' : 'v-autocomplete'"
      v-model="user"
      :items="computedUsers"
      :search-input.sync="search"
      item-text="item.node.userProfile.username"
      hide-details
      dense
      clearable
      outlined
      solo
      flat
      :append-icon="null"
      prepend-inner-icon="$search"
      clear-icon="$close"
      :persistent-placeholder="false"
      :filter="
        (item, queryText, itemText) => { 
          return item.node.userProfile.username.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
        }
      "
      :loading="loading"
      item-value="username"
      class="choose-user-input"
      :placeholder="label"
      :menu-props="{
        contentClass: 'story-city-select-content',
      }"
    >
      <template v-slot:selection="{ item }">
        <v-chip
          class="selected-chip ma-0 mr-1"
          color="primaryLight"
          dark
        >
          <span v-if="typeof item == 'object'">
            <v-avatar>
              <v-img v-if="item.node.userProfile.image" :src="item.node.userProfile.image" />
              <v-icon v-else>mdi-account-circle</v-icon>
            </v-avatar>
            {{ $t('fullName', { firstName: item.node.userProfile.firstName, lastName: item.node.userProfile.lastName } )}}
          </span>
          <span v-else-if="typeof item == 'string'">
            {{ item }}
          </span>
        </v-chip>
      </template>
      <template v-slot:item="{ item: { node } }">
        <v-list-item-avatar class="avatar ma-0 mr-2" :size="avatarSize">
          <img v-if="node.userProfile.avatar" :src="node.userProfile.avatar" />
          <v-icon v-else :size="avatarSize">mdi-account-circle</v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="mb-1">
            <span class="regular-action-heading">{{ $t('fullName', { firstName: node.userProfile.firstName, lastName: node.userProfile.lastName } )}}</span>
          </v-list-item-title>
          <v-list-item-subtitle class="username-text" v-html="node.userProfile.username"></v-list-item-subtitle>
        </v-list-item-content>
      </template>
      <template v-slot:append-item>
        <div v-intersect="endIntersect" />
      </template>
    </component>
</template>

<script>
import { VCombobox, VAutocomplete } from "vuetify/lib";
import { SEARCH_USER } from "@/graphql/UserQueries";
import { MAX_CONTRIBUTOR_WEBSITE_LENGTH } from "@/const";
const RESULTS_TO_SHOW = 5
const AVATAR_SIZE = 38

export default {
  props: {
    canAdd: {
      type: Boolean,
      default: false,
    },
    value: [Object, String],
    label: String,
    role: { type: String, default: null }
  },
  components: { VCombobox, VAutocomplete },
  apollo: {
    users: {
      query: SEARCH_USER,
      fetchPolicy: "no-cache",
      debounce: 500,
      variables() { 
        return  {
          username: this.search,
          numberToShow: RESULTS_TO_SHOW,
          cursor: null,
          role: this.role,
        }
      },
      watchLoading(isLoading) {
        this.loading = isLoading
      },
      skip() {
        if (this.search) {
          return !(this.search.length > 1)
        }
        return true
      },
    },
  },
  data() {
    return {
      user: this.value,
      search: "",
      cursor: null,
      loading: false,
    };
  },
  watch: {
    user(newValue) {
      let emit = newValue
      if (newValue && newValue.node) {
        emit = newValue.node
      }
      this.$emit("input", emit);
    },
    tooLong(newValue) {
      if (newValue) {
        this.$emit("error", this.$t('storyHub.publishing.wizard.contributorTextTooLong'))
      }
      else {
        this.$emit("error", null);
      }
    },
    value(newValue) {
      if (this.user && this.user.node !== newValue) {
        if (newValue == null) {
          this.user = null
          this.search = ""
        }
        else if (typeof newValue == "string") {
          return
        }
        else {
          this.user = { node: newValue };
        }
      }
    },
    search(newValue) {
      this.$emit("search", newValue);
    },
  },
  methods: {
    endIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && this.users && this.users.pageInfo.hasNextPage) {
        let cursor = this.users.pageInfo.endCursor
        
        this.$apollo.queries.users.fetchMore({
          variables: { cursor: cursor, role: this.role},
          updateQuery: (previousResult, { fetchMoreResult }) => {
            let edges = [
              ...previousResult.users.edges,
              ...fetchMoreResult.users.edges,
            ]

            let pageInfo = fetchMoreResult.users.pageInfo;
            return { 
              users: {
                edges: edges,
                pageInfo: pageInfo,
                __typename: previousResult.users.__typename,
              }
            }
          }
        })
      }
    },
    filter(item, queryText) {
      return item.node.userProfile.username.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
    }
  },
  computed: {
    tooLong() {
      if (typeof this.value == "string" && this.value.length > MAX_CONTRIBUTOR_WEBSITE_LENGTH) {
        return true
      }
      if (this?.search?.length > MAX_CONTRIBUTOR_WEBSITE_LENGTH) {
        return true
      }
      return false
    },
    computedUsers() {
      if (this.users){
        return this.users.edges
      }
      return []
    },
    skip() {
      if (this.search) {
        return this.search.length > 1
      }
      return false
    },
    avatarSize() {
      return AVATAR_SIZE;
    },
  }
};
</script>

<style lang="scss" scoped>
.selected-chip {
  font-weight: 500;
  line-height: normal;
}

.username-text {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: $font-spacing;
}
</style>